import { useState, useEffect } from 'react';
import { useDebug } from '../context/DebugContext';
import { useIdentities, useSessions } from '../hooks/useHttpPolling';
import './Identity.css';

export default function Identity() {
  const { addEvent } = useDebug();
  const { data: identitiesData, loading: identitiesLoading, error: identitiesError } = useIdentities();
  const { data: sessionsData, loading: sessionsLoading, error: sessionsError } = useSessions();
  const [selectedIdentity, setSelectedIdentity] = useState<string | null>(null);

  const identities = identitiesData?.identities || [];
  const sessions = sessionsData?.sessions || [];
  const loading = identitiesLoading || sessionsLoading;
  const error = identitiesError || sessionsError;

  useEffect(() => {
    addEvent({
      source: 'console',
      category: 'navigation',
      message: 'Viewed Identity page',
    });
  }, [addEvent]);

  const handleRevokeSession = async (sessionId: string) => {
    addEvent({
      source: 'console',
      category: 'api',
      message: `Revoking session: ${sessionId}`,
      data: { sessionId },
    });
    
    try {
      const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
      await fetch(`${edgeUrl}/api/identity/sessions/${sessionId}`, {
        method: 'DELETE',
      });
      // Session will be removed on next poll
    } catch (e) {
      console.error('Failed to revoke session:', e);
    }
  };

  return (
    <div className="identity-page">
      <div className="page-header">
        <h2>Identity Management</h2>
        <button className="btn-primary">+ Add Identity</button>
      </div>

      {error ? (
        <div className="error">Error loading identities: {String(error)}</div>
      ) : loading ? (
        <div className="loading">Loading identities...</div>
      ) : (
        <div className="identity-layout">
          <div className="identities-panel">
            <h3>Identities</h3>
            <div className="identity-list">
              {identities.map((identity) => (
                <div
                  key={identity.id}
                  className={`identity-card ${selectedIdentity === identity.id ? 'selected' : ''}`}
                  onClick={() => setSelectedIdentity(identity.id)}
                >
                  <div className="identity-avatar">
                    {identity.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="identity-info">
                    <span className="identity-name">{identity.displayName}</span>
                    <span className="identity-key">{identity.publicKeyHash}</span>
                    <div className="identity-roles">
                      {identity.roles.map((role) => (
                        <span key={role} className="role-tag">{role}</span>
                      ))}
                    </div>
                  </div>
                  <div className="identity-meta">
                    <span className="session-count">{identity.sessions} sessions</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="sessions-panel">
            <h3>Active Sessions</h3>
            <div className="session-list">
              {sessions.map((session) => (
                <div key={session.id} className="session-card">
                  <div className="session-icon">
                    {session.deviceName.includes('iPhone') ? '📱' : '💻'}
                  </div>
                  <div className="session-info">
                    <span className="session-device">
                      {session.deviceName}
                      {session.current && (
                        <span className="current-badge">Current</span>
                      )}
                    </span>
                    <span className="session-ip">{session.ipAddress}</span>
                    <span className="session-time">
                      Last active: {new Date(session.lastActive).toLocaleString()}
                    </span>
                  </div>
                  {!session.current && (
                    <button
                      className="btn-revoke"
                      onClick={() => handleRevokeSession(session.id)}
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
